import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Sparkles, Search, Upload, Loader2, X, Plus, Bookmark, Wand2, Database, Radar, SlidersHorizontal } from "lucide-react";
import { api, errorCode, errorDetail } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import CandidateCard from "@/components/CandidateCard";
import CandidatePanel from "@/components/CandidatePanel";
import SaveToDialog from "@/components/SaveToDialog";
import { EmptyState, Pill } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { tk } from "@/i18n";
import { fmtNumber } from "@/i18n/format";
import { JOB_LABEL, STATUS_LABEL } from "@/pages/app/AIDiscovery";

const EMPTY = { target_roles: [], mandatory_skills: [], preferred_skills: [], countries: [], cities: [], regions: [], languages: [], industries: [], education: [], certifications: [], keywords: [], min_years: null, max_years: null, radius_km: 30 };
const CHIP_GROUPS = [["target_roles", tk("Roles")], ["mandatory_skills", tk("Must-have skills")], ["preferred_skills", tk("Nice-to-have")], ["countries", tk("Countries (ISO)")], ["cities", tk("Cities")], ["regions", tk("Regions")], ["languages", tk("Languages (ISO)")], ["industries", tk("Industries")], ["education", tk("Education")], ["keywords", tk("Keywords")]];

function ChipGroup({ k, label, values, onChange }) {
  const { t } = useI18n();
  const [draft, setDraft] = useState("");
  const add = () => { if (draft.trim()) { onChange([...values, draft.trim()]); setDraft(""); } };
  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid={`chips-${k}`}>
      <span className="text-[11px] font-semibold text-muted-foreground w-28 shrink-0">{t(label)}</span>
      {values.map((v, i) => (
        <span key={i} className={`inline-flex items-center gap-1 rounded-full pl-2.5 pr-1 py-0.5 text-xs font-medium ${k === "mandatory_skills" ? "bg-primary text-primary-foreground" : "bg-accent text-accent-foreground"}`}>
          {v}<button onClick={() => onChange(values.filter((_, j) => j !== i))} data-testid={`chip-remove-${k}-${i}`} className="rounded-full p-0.5 hover:bg-black/10"><X className="w-3 h-3" /></button>
        </span>
      ))}
      <input value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder={t("+ add")}
        data-testid={`chip-add-${k}`} className="h-6 w-20 bg-transparent text-xs outline-none placeholder:text-muted-foreground/60 focus:w-32 transition-[width]" />
    </div>
  );
}

const SEL_F = "h-8 rounded-md border bg-background px-2 text-xs";

function FiltersPanel({ intent, setIntent, saved, onLoadSaved }) {
  const { t } = useI18n();
  const set = (k, v) => setIntent({ ...intent, [k]: v });
  const num = (k) => (e) => set(k, e.target.value === "" ? null : Number(e.target.value));
  return (
    <div className="card-surface px-3 py-2.5 flex flex-wrap items-end gap-x-4 gap-y-2" data-testid="search-filters-panel">
      <div className="eyebrow flex items-center gap-1.5 self-center"><SlidersHorizontal className="w-3 h-3" />{t("Filters")}</div>
      <label className="text-[11px] font-medium text-muted-foreground">{t("Min. years")}<Input type="number" min={0} value={intent.min_years ?? ""} onChange={num("min_years")} className="h-8 w-20 mt-0.5 text-xs" data-testid="filter-min-years" /></label>
      <label className="text-[11px] font-medium text-muted-foreground">{t("Max. years")}<Input type="number" min={0} value={intent.max_years ?? ""} onChange={num("max_years")} className="h-8 w-20 mt-0.5 text-xs" data-testid="filter-max-years" /></label>
      <label className="text-[11px] font-medium text-muted-foreground flex flex-col">{t("Declared availability")}
        <select value={intent.availability || ""} onChange={(e) => set("availability", e.target.value || null)} className={`${SEL_F} mt-0.5`} data-testid="filter-availability">
          <option value="">{t("Any")}</option><option value="open">{t("Open to opportunities")}</option><option value="passive">{t("Passive")}</option>
        </select></label>
      <label className="text-[11px] font-medium text-muted-foreground flex flex-col">{t("Verification")}
        <select value={intent.verification_status || ""} onChange={(e) => set("verification_status", e.target.value || null)} className={`${SEL_F} mt-0.5`} data-testid="filter-verification">
          <option value="">{t("Any")}</option><option value="verified_by_candidate">{t("Verified by candidate")}</option><option value="unverified">{t("Unverified")}</option>
        </select></label>
      <label className="text-[11px] font-medium text-muted-foreground flex flex-col">{t("Data freshness")}
        <select value={intent.freshness_days || ""} onChange={(e) => set("freshness_days", e.target.value ? Number(e.target.value) : null)} className={`${SEL_F} mt-0.5`} data-testid="filter-freshness">
          <option value="">{t("Any")}</option><option value="30">{t("Last 30 days")}</option><option value="180">{t("Last 6 months")}</option><option value="365">{t("Last 12 months")}</option>
        </select></label>
      <label className="text-[11px] font-medium text-muted-foreground flex flex-col sm:ml-auto"><span className="flex items-center gap-1"><Bookmark className="w-3 h-3" />{t("Saved Searches")}</span>
        <select value="" onChange={(e) => { const s = saved.find((x) => x.id === e.target.value); if (s) onLoadSaved(s); }} className={`${SEL_F} mt-0.5 max-w-[220px]`} data-testid="load-saved-select">
          <option value="">{saved.length ? t("Choose…") : t("None yet.")}</option>{saved.map((s) => <option key={s.id} value={s.id}>{s.title}</option>)}
        </select></label>
    </div>
  );
}

export default function TalentSearch() {
  const { t } = useI18n();
  const { refresh, setUpgradeOpen } = useAuth();
  const [params] = useSearchParams();
  const [text, setText] = useState(params.get("q") || "");
  const [intent, setIntent] = useState(EMPTY);
  const [parsed, setParsed] = useState(false);
  const [busy, setBusy] = useState(null);
  const [res, setRes] = useState(null);
  const [page, setPage] = useState(1);
  const [sel, setSel] = useState(null);
  const [saveFor, setSaveFor] = useState(null);
  const [saved, setSaved] = useState([]);
  const [exa, setExa] = useState(null);
  const [job, setJob] = useState(null);
  const fileRef = useRef();

  useEffect(() => {
    api.get("/saved-searches").then((r) => setSaved(r.data));
    api.get("/ai/status").then((r) => setExa(r.data));
  }, []);

  useEffect(() => {
    if (!job || !["queued", "running"].includes(job.status)) return;
    const h = setTimeout(() => api.get(`/ai/jobs/${job.id}`).then((r) => { setJob(r.data); if (!["queued", "running"].includes(r.data.status)) refresh(); }), 2500);
    return () => clearTimeout(h);
  }, [job, refresh]);

  const interpret = async () => {
    setBusy("parse");
    try {
      const { data } = await api.post("/search/parse", { text, kind: text.length > 600 ? "job_description" : "natural" });
      setIntent({ ...EMPTY, ...data.intent });
      setParsed(true);
    } catch (e) { toast.error(errorDetail(e)); } finally { setBusy(null); }
  };

  const uploadJD = async (file) => {
    setBusy("parse");
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/search/parse-document", fd);
      setIntent({ ...EMPTY, ...data.intent });
      setText(`[${t("Job description")}] ${data.intent.job_title || file.name}`);
      setParsed(true);
      toast.success(t("Extracted {{count}} characters from {{file}}", { count: fmtNumber(data.extracted_chars), file: file.name }));
    } catch (e) { toast.error(errorDetail(e)); } finally { setBusy(null); }
  };

  const execute = async (p = 1, it = intent) => {
    setBusy("search");
    try {
      const { data } = await api.post("/search/execute", { intent: it, query_text: text, mode: parsed ? "natural" : "advanced", page: p, page_size: 20 });
      setRes(data);
      setPage(p);
    } catch (e) { toast.error(errorDetail(e)); } finally { setBusy(null); }
  };

  const discover = async (effort) => {
    setBusy("discover");
    try {
      const { data } = await api.post("/ai/discover", { intent, effort, idempotency_key: `${Date.now()}` });
      setJob(data);
      refresh();
      toast.success(t("{{mode}} started — {{count}} credits reserved", { mode: t(effort === "live" ? "Live discovery" : "Deep search"), count: data.reserved_credits }));
    } catch (e) {
      if (errorCode(e) === "upgrade_required") setUpgradeOpen(true); else toast.error(errorDetail(e));
    } finally { setBusy(null); }
  };

  const saveSearch = async () => {
    const title = window.prompt(t("Name this search"), intent.job_title || text.slice(0, 60));
    if (!title) return;
    try {
      const { data } = await api.post("/saved-searches", { title, query_text: text, intent, mode: "natural" });
      setSaved([data, ...saved]);
      toast.success(t("Search saved"));
    } catch (e) { toast.error(errorDetail(e)); }
  };

  const hasCriteria = CHIP_GROUPS.some(([k]) => intent[k]?.length) || intent.min_years != null;

  return (
    <div className="flex">
      <section className="flex-1 min-w-0 p-4 sm:p-6 space-y-4">
        <FiltersPanel intent={intent} setIntent={setIntent} saved={saved} onLoadSaved={(s) => { setText(s.query_text || ""); setIntent({ ...EMPTY, ...s.intent }); setParsed(true); execute(1, { ...EMPTY, ...s.intent }); }} />
        <div className="rounded-2xl border bg-card p-3 focus-within:ring-2 focus-within:ring-primary/40 transition-shadow ai-ring" data-testid="ai-search-box">
          <div className="flex items-start gap-2">
            <Sparkles className="w-5 h-5 text-primary mt-2 shrink-0" />
            <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder={t("e.g. Senior robotics engineer, ROS2 + C++, Lyon or remote EU, 5+ years")} data-testid="ai-search-input"
              onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) interpret(); }}
              className="flex-1 resize-none bg-transparent outline-none text-[15px] leading-relaxed placeholder:text-muted-foreground/70 py-1.5" />
          </div>
          <div className="flex flex-wrap items-center gap-2 mt-2 pl-7">
            <Button size="sm" onClick={interpret} disabled={text.trim().length < 3 || !!busy} className="ai-gradient border-0" data-testid="interpret-button">
              {busy === "parse" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}{t("Interpret")}
            </Button>
            <Button size="sm" variant="outline" onClick={() => fileRef.current.click()} disabled={!!busy} data-testid="upload-jd-button"><Upload className="w-4 h-4" />{t("Upload job description")}</Button>
            <input ref={fileRef} type="file" accept=".pdf,.docx,.txt" hidden onChange={(e) => e.target.files[0] && uploadJD(e.target.files[0])} data-testid="upload-jd-input" />
            <span className="text-[11px] text-muted-foreground ml-auto hidden sm:block">{t("Interpretation is free · review criteria before any paid AI search")}</span>
          </div>
        </div>

        {(parsed || hasCriteria) && (
          <div className="card-surface p-4 space-y-2 fade-up" data-testid="interpreted-criteria">
            <div className="flex items-center justify-between mb-1">
              <div className="eyebrow">{t("Interpreted criteria")}{intent.job_title ? ` · ${intent.job_title}` : ""}</div>
              <button onClick={saveSearch} data-testid="save-search-button" className="text-xs font-medium text-primary flex items-center gap-1"><Bookmark className="w-3.5 h-3.5" />{t("Save search")}</button>
            </div>
            {CHIP_GROUPS.map(([k, label]) => <ChipGroup key={k} k={k} label={label} values={intent[k] || []} onChange={(v) => setIntent({ ...intent, [k]: v })} />)}
            {intent.cities?.length > 0 && (
              <div className="flex items-center gap-3 py-1.5 text-xs" data-testid="radius-row">
                <span className="w-28 shrink-0 text-muted-foreground">{t("Search radius")}</span>
                <select value={intent.radius_km || 30} onChange={(e) => setIntent({ ...intent, radius_km: Number(e.target.value) })} className="h-7 rounded-md border bg-background px-2 text-xs" data-testid="radius-select">
                  {[10, 30, 50, 100].map((r) => <option key={r} value={r}>{t("within {{km}} km of {{city}}", { km: r, city: intent.cities.join(" / ") })}</option>)}
                </select>
              </div>
            )}
            <div className="flex flex-wrap gap-2 pt-3 border-t mt-3">
              <Button size="sm" onClick={() => execute(1)} disabled={!!busy} data-testid="search-catalog-button">
                {busy === "search" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Database className="w-4 h-4" />}{t("Search catalog")} <span className="text-[10px] opacity-75 ml-1">{t("{{count}} credits", { count: 0 })}</span>
              </Button>
              <Button size="sm" variant="outline" onClick={() => discover("live")} disabled={!!busy || !exa?.configured} data-testid="exa-live-discovery-button">
                <Radar className="w-4 h-4" />{t("Live discovery")} <span className="text-[10px] text-muted-foreground ml-1">{t("{{count}} credits", { count: exa?.tariffs?.live ?? "…" })}</span>
              </Button>
              <Button size="sm" variant="outline" onClick={() => discover("deep")} disabled={!!busy || !exa?.configured} data-testid="exa-deep-search-button">
                {t("Deep search")} <span className="text-[10px] text-muted-foreground ml-1">{t("{{count}} credits", { count: exa?.tariffs?.deep ?? "…" })}</span>
              </Button>
              {exa && !exa.configured && <Pill tone="amber" testid="exa-not-configured">{t("Discovery Engine not activated.")}</Pill>}
            </div>
          </div>
        )}

        {job && (
          <div className="card-surface p-4 flex items-center gap-3" data-testid="exa-job-status">
            {["queued", "running"].includes(job.status) ? <Loader2 className="w-4 h-4 animate-spin text-primary" /> : <Radar className="w-4 h-4 text-primary" />}
            <div className="text-sm flex-1">{t(JOB_LABEL[job.job_type] || job.job_type)} · <b>{t(STATUS_LABEL[job.status] || job.status)}</b> · {t("{{count}} profiles", { count: job.lead_count })} · {job.status === "completed" ? t("{{count}} credits charged", { count: job.charged_credits }) : t("{{count}} credits reserved", { count: job.reserved_credits })}</div>
            <a href="/app/discovery" className="text-xs text-primary font-medium">{t("Open AI Discovery →")}</a>
          </div>
        )}

        {busy === "search" && !res && <div className="space-y-3">{[1, 2, 3].map((i) => <div key={i} className="h-28 rounded-xl bg-muted shimmer" />)}</div>}

        {res && (
          <div className="space-y-3" data-testid="search-results">
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span data-testid="results-count">{t("{{count}} matches in your catalog · {{ms}} ms", { count: res.total, ms: res.latency_ms })}</span>
              <span>{t("Ranked suggestions — human review required")}</span>
            </div>
            {res.items.length === 0 ? <EmptyState icon={Search} title={t("No results found")} body={t("Broaden the criteria, import your talent pool, or launch a TopTalent Discovery run.")} testid="no-results" /> :
              res.items.map((c, i) => <CandidateCard key={c.id} c={c} idx={i} selected={sel?.id === c.id} onSelect={setSel} onSave={setSaveFor} onContact={setSel} />)}
            {res.total > res.page_size && (
              <div className="flex justify-center gap-2 pt-2">
                <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => execute(page - 1)} data-testid="prev-page">{t("Previous")}</Button>
                <span className="text-xs self-center">{t("Page {{page}} / {{total}}", { page, total: Math.ceil(res.total / res.page_size) })}</span>
                <Button size="sm" variant="outline" disabled={page * res.page_size >= res.total} onClick={() => execute(page + 1)} data-testid="next-page">{t("Next")}</Button>
              </div>
            )}
          </div>
        )}

        {!res && !parsed && !busy && (
          <div className="grid sm:grid-cols-3 gap-3 pt-4">
            {[tk("Senior robotics engineers with 5+ years ROS2 and C++, France or Germany, fluent English"), tk("Embedded systems engineer, C++, Lyon, 3+ years"), tk("Cloud engineer Azure + Kubernetes, remote within EU")].map((ex, i) => (
              <button key={i} onClick={() => setText(t(ex))} data-testid={`example-query-${i}`} className="text-left card-surface p-4 text-sm hover:border-primary/40 transition-colors">
                <Plus className="w-4 h-4 text-primary mb-2" />{t(ex)}
              </button>
            ))}
          </div>
        )}
      </section>
      {sel && (
        <aside className="fixed inset-0 z-40 bg-background lg:static lg:z-auto lg:w-[420px] shrink-0 lg:border-l lg:h-[calc(100vh-3.5rem)] lg:sticky lg:top-14 overflow-y-auto" data-testid="candidate-detail-panel">
          <CandidatePanel id={sel.id} match={sel.match} onClose={() => setSel(null)} />
        </aside>
      )}
      <SaveToDialog candidate={saveFor} onClose={() => setSaveFor(null)} />
    </div>
  );
}
